import { useBundle } from '@amzn/react-arb-tools';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import {
  Spinner,
  SpinnerColorScheme,
  SpinnerSize,
} from '@amzn/stencil-react-components/spinner';
import { H1, Text, TextVariant } from '@amzn/stencil-react-components/text';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { trackClick } from 'src/analytics/clickTracking';
import { recordRumEvent } from 'src/analytics/rum';
import { OPT_IN_MESSAGE_BUNDLE_NAME } from 'src/constants';
import {
  OPT_IN_PAGE_GROUP,
  PAGE_LOAD_EVENT,
} from 'src/constants/MonitoringConstants';
import { useOptIn } from 'src/hooks/useOptIn';
import { highlightedText } from 'src/utils/textUtils';
import { t } from '../helpers';

const OptInPage = () => {
  const [bundle] = useBundle(OPT_IN_MESSAGE_BUNDLE_NAME);
  const { confirmationToken } = useParams();
  const { mutateAsync: optInMutation } = useOptIn('opt_in_page');

  const [optedIn, setOptedIn] = useState<boolean>(false);
  const [optInFailed, setOptInFailed] = useState<boolean>(false);
  const [optInLoading, setOptInLoading] = useState<boolean>(false);

  useEffect(() => {
    recordRumEvent(PAGE_LOAD_EVENT, { pageGroup: OPT_IN_PAGE_GROUP });
  }, []);

  const optIn = async (): Promise<void> => {
    if (optInLoading) {
      return;
    }

    trackClick({
      elementId: 'opt_in',
      elementType: 'button',
      pageGroup: OPT_IN_PAGE_GROUP,
    });

    setOptInLoading(true);
    setOptInFailed(false);

    await optInMutation({ confirmationToken: confirmationToken ?? '' })
      .then(() => setOptedIn(true))
      .catch(() => setOptInFailed(true))
      .finally(() => setOptInLoading(false));
  };

  const header = highlightedText(
    bundle,
    optedIn ? 'opt_in_success_header_prefix' : 'opt_in_header_prefix',
    'opt_in_header_in_this_together',
    'opt_in_header_postfix',
  );

  return (
    <Col
      dataTestId="grow-thrive-connect-opt-in-test-id"
      backgroundColor="color.surface.dark-bg-static"
      alignItems="center"
      padding="dimensions.spacing.700"
    >
      <Card maxWidth={640} width="100%">
        <Col
          width="100%"
          gridGap="dimensions.spacing.300"
          padding="dimensions.spacing.400"
        >
          {optInFailed && (
            <MessageBanner
              type={MessageBannerType.Error}
              aria-live="assertive"
              isDismissible
              dataTestId="opt-in-error-banner-test-id"
              onDismissed={() => setOptInFailed(false)}
            >
              <Text>{t(bundle, 'opt_in_failure')}</Text>
            </MessageBanner>
          )}
          {!optedIn ? (
            <>
              <H1 variant={TextVariant.HeadingMD}>{header}</H1>
              <Text>{t(bundle, 'opt_in_description')}</Text>
              <Row>
                <Button
                  variant={ButtonVariant.Primary}
                  dataTestId="opt-in-button-test-id"
                  onClick={() => optIn()}
                >
                  {optInLoading && (
                    <Spinner
                      size={SpinnerSize.Small}
                      style={{ marginRight: '8px' }}
                      colorScheme={SpinnerColorScheme.Inverted}
                    />
                  )}
                  <Text textAlign="center">{t(bundle, 'opt_in_button')}</Text>
                </Button>
              </Row>
            </>
          ) : (
            <Text
              variant={TextVariant.HeadingMD}
              dataTestId="opt-in-success-test-id"
            >
              {header}
            </Text>
          )}
        </Col>
      </Card>
    </Col>
  );
};

export default OptInPage;
